import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';

import head from 'lodash/head';

import Typography from '@mui/material/Typography';

import { useGetSimilarVaults } from '@services/api/vaults/vaults.api';

import { modalActions } from '@contexts/common/ModalContext';

import { useMediaQuery } from '@hooks/common/useMediaQuery';
import useFarmsList from '@hooks/vaults/useFarmsList';
import useTablesHeight from '@hooks/vaults/useTablesHeight';

import { toBN } from '@utils/formaters.utils';

import Button from '@components/common/Button';
import FarmsFilters from '@components/vaults/FarmsFilters';
import MaxFarms from '@components/vaults/MaxFarms';

import AllFarms from '@pages/CreateVault/VaultConstructor/AllFarms';
import SelectedFarms from '@pages/CreateVault/VaultConstructor/SelectedFarms';

import {
  AVAILABLE_CHAINS_LIST,
  CHAIN_ID,
  MAX_FARMS_COUNT,
} from '@constants/common/chain.constants';
import { CHAIN_FILTER_PARAM } from '@constants/common/params.constants';
import {
  PATH_CREATE_VAULT,
  PATH_SIMILAR_VAULTS,
} from '@constants/common/routes.constants';
import { Variant } from '@constants/common/ui.constants';
import { FARM_IDs_PARAM } from '@constants/vaults/params.constants';

import { TokensDto } from '@dto/vaults/tokens.dto';
import { FarmDto } from '@dto/vaults/vault-factory.dto';

import { FarmCard } from '@app-types/vaults/farm.types';

import useStyles from './styles.module';

interface Props {
  tokens: TokensDto;
  defaultChainId?: number;
  defaultFarms?: FarmCard[];
}

const ViewAllFarms: FC<Props> = ({ tokens, defaultFarms, defaultChainId }) => {
  const { classes } = useStyles();

  const navigate = useNavigate();
  const { laptopView, isMobile, isDesktop } = useMediaQuery();

  const refSelectedFarms = useRef<HTMLDivElement>(null);
  const refContent = useRef<HTMLDivElement>(null);
  const refRender = useRef<boolean>(false);

  const [contentHeight, setContentHeight] = useState<number>(0);
  const [selectedTableHeight, setSelectedTableHeight] = useState<number>(0);

  const [selectedChains, setSelectedChains] = useState(
    defaultChainId ? [defaultChainId] : AVAILABLE_CHAINS_LIST,
  );
  const [selectedFarms, setSelectedFarms] = useState<FarmCard[]>(
    defaultFarms || [],
  );

  const { filters, actions, totalCount, isPending, farms } = useFarmsList({
    selectedChains,
  });

  const selectedFarmsIds = useMemo(
    () => selectedFarms.map((farm) => farm.farm.id),
    [selectedFarms],
  );

  const selectedChainId = useMemo(() => head(selectedChains), [selectedChains]);

  const {
    data: similarVaults,
    isPending: isPendingSimilar,
    isError,
  } = useGetSimilarVaults({
    chainId: selectedChainId,
    farmIds: selectedFarmsIds,
  });

  const isMaxFarmsCount = useMemo(() => {
    const count = head(selectedChains) as CHAIN_ID;
    return selectedFarms.length === MAX_FARMS_COUNT[count];
  }, [selectedChains, selectedFarms]);

  const disabledSimilarVaultBtn = useMemo(
    () => !similarVaults?.length || isError || isPendingSimilar,
    [isError, isPendingSimilar, similarVaults?.length],
  );

  const disabledCreateVaultBtn = useMemo(() => {
    const count = head(selectedChains) as CHAIN_ID;
    return (
      !!selectedFarms.length && selectedFarms.length <= MAX_FARMS_COUNT[count]
    );
  }, [selectedChains, selectedFarms.length]);

  const { tableHeightAllFarms, tableHeightSelectedFarms } = useTablesHeight({
    isMaxFarmsCount,
    selectedFarms,
    contentHeight,
    refSelectedFarms: selectedTableHeight,
    refContent: refContent?.current?.clientHeight || null,
  });

  const onDeleteFarm = useCallback((id: number) => {
    setSelectedFarms((prevState) => {
      const farmsLength = prevState.length - 1;
      if (farmsLength <= 0) {
        setSelectedChains(AVAILABLE_CHAINS_LIST);
      }
      return prevState.filter((farm) => farm.farm.id !== id);
    });
  }, []);

  const onSelectFarm = useCallback(
    (farm: FarmDto) => {
      if (isMaxFarmsCount) return;
      setSelectedFarms((prevState) => [...prevState, { farm, percent: 0 }]);
    },
    [isMaxFarmsCount],
  );

  const onClearStates = useCallback(() => {
    actions.onClearFilters();
    setSelectedFarms([]);
  }, []);

  const onChangeChain = useCallback((chainId: number) => {
    setSelectedChains((prevState) => {
      if (prevState.includes(chainId)) {
        if (prevState.length === AVAILABLE_CHAINS_LIST.length) {
          return prevState.filter((item) => item === chainId);
        }

        if (
          prevState.length === 1 &&
          toBN(chainId).equals(toBN(head(prevState)))
        ) {
          return [...AVAILABLE_CHAINS_LIST];
        }
        return prevState.filter((item) => item !== chainId);
      }
      return [...prevState, chainId];
    });
    onClearStates();
  }, []);

  const onDefaultChains = useCallback(() => {
    setSelectedChains(AVAILABLE_CHAINS_LIST);
    onClearStates();
  }, []);

  const queryParams = useMemo(() => {
    const selectedChainIds =
      selectedChains.length > 0 ? JSON.stringify(selectedChains) : null;
    return selectedChainIds
      ? `${CHAIN_FILTER_PARAM}=${encodeURIComponent(selectedChainIds)}&${FARM_IDs_PARAM}=${selectedFarmsIds.join(',')}`
      : '';
  }, [selectedFarmsIds, selectedChains]);

  const onSimilarVaults = useCallback(() => {
    if (!similarVaults?.length) return;
    modalActions.hide();
    navigate(`${PATH_SIMILAR_VAULTS}/?${queryParams}`);
  }, [navigate, queryParams, similarVaults]);

  const onCreateVault = useCallback(() => {
    if (!queryParams) return;
    modalActions.hide();
    navigate(`${PATH_CREATE_VAULT}/?${queryParams}`);
  }, [navigate, queryParams]);

  useEffect(() => {
    if (refContent?.current?.clientHeight && !refRender?.current) {
      refRender.current = true;
      setContentHeight(refContent.current.clientHeight);
    }
  }, [refContent?.current?.clientHeight]);

  useEffect(() => {
    if (!selectedFarms.length) {
      setSelectedTableHeight(0);
    }
    if (refSelectedFarms?.current) {
      setSelectedTableHeight(refSelectedFarms?.current?.clientHeight ?? 0);
    }
  }, [selectedFarms]);

  useEffect(() => {
    if (selectedFarms.length === 1) {
      const selectedFarm = head(selectedFarms);

      if (selectedFarm) {
        const _chainId = selectedFarm.farm.chainId;
        setSelectedChains([_chainId]);
      }
    }
  }, [selectedFarms]);

  return (
    <div className={classes.box}>
      {!laptopView && (
        <Typography variant="h3" align="left" className={classes.title}>
          Find a Farm
        </Typography>
      )}
      <FarmsFilters
        filters={filters}
        actions={actions}
        selectedChains={selectedChains}
        onChangeChain={onChangeChain}
        onDefaultChains={onDefaultChains}
        className={classes.filters}
      />
      <div ref={refContent} className={classes.content}>
        {!!selectedFarms.length && (
          <div ref={refSelectedFarms} className={classes.selectedTableBox}>
            <SelectedFarms
              isSideBarView
              tokens={tokens}
              selectedFarms={selectedFarms}
              onDeleteFarm={onDeleteFarm}
              tableHeight={isDesktop ? tableHeightSelectedFarms : undefined}
            />
            {isMaxFarmsCount && <MaxFarms />}
          </div>
        )}
        {!!refContent?.current?.clientHeight && (
          <AllFarms
            isSideBarView
            tokens={tokens}
            farms={farms}
            totalCount={totalCount}
            isPending={isPending}
            selectedFarms={selectedFarms}
            onSelectFarm={onSelectFarm}
            isDisabledSelect={isMaxFarmsCount}
            tableHeight={isDesktop ? tableHeightAllFarms : isMobile ? 360 : 560}
            className={classes.allFarms}
          />
        )}
        <div className={classes.btnBox}>
          <Button
            fullWidth
            variant={Variant.Contained}
            disabled={disabledSimilarVaultBtn}
            onClick={onSimilarVaults}
          >
            {similarVaults?.length || 0} Similar Vaults
          </Button>
          <Button
            fullWidth
            disabled={!disabledCreateVaultBtn}
            variant={Variant.Gradient}
            onClick={onCreateVault}
          >
            Create a new vault
          </Button>
        </div>
      </div>
    </div>
  );
};

export default memo(ViewAllFarms);
